<template>
    <div>
    <v-dialog v-model="show" persistent max-width="80%" style="border-radius: 10px">
        <v-card>
            <v-card-title class="headline">
                <span style="color: #4a4a4a">{{ $t('csv.resultDialog.title') }}</span>
            </v-card-title>
            <v-card-text>
                <p v-if="allowRowActions" class="body-2 mb-2">
                    {{ $t('csv.resultDialog.summary', { total: items.length, errors: errorRowCount }) }}
                </p>
                <v-alert v-if="allowRowActions && errorRowCount > 0"
                    type="error"
                    dense
                    outlined
                    class="mb-3"
                >
                    {{ $t('system.messages.fileProcessedWarning') }}
                    <div class="mt-1 body-2">{{ $t('csv.resultDialog.saveWithErrorsHint') }}</div>
                </v-alert>
                <v-data-table 
                    :headers="headersWithProperties" 
                    :items="items" 
                    sort-by="line_number" 
                    dense 
                    class="elevation-7"
                    locale="pt" 
                    style="border-radius: 10px"
                    loading-text="Carregando Resultados..."
                    :item-key="itemKeyField"
                    :footer-props="{ itemsPerPageOptions: [10, 15, 25, 50] }"
                >
                    <template v-slot:item="{ item }">
                        <tr :class="{
                            'csv-result-row--edited': isEditedRow(item),
                            'csv-result-row--error': rowHasError(item),
                        }">
                            <td v-for="(header, h) in displayHeaders" :key="`HEADER-${h}`">
                                <template v-if="header.value === 'acoes'">
                                    <div
                                        v-if="allowRowActions && rowHasError(item)"
                                        class="d-flex flex-wrap justify-center"
                                        style="gap: 2px"
                                    >
                                        <v-btn color="green" icon small @click="openRowEdit(item)">
                                            <v-icon small>mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn color="red" icon small @click="deleteRow(item)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </template>
                                <CSVItem
                                    v-else
                                    :value="getDisplayValue(header, item)"
                                    :error="item[`${header.error}`]"
                                    :warning="editedRowWarning(item, header)"
                                />
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
            <v-card-actions>
                <strong :style="{ color: messageIsError ? 'red' : '#4a4a4a' }">{{ message }}</strong>
                <v-spacer></v-spacer>
                <v-btn v-if="hasLogToDownload" color="primary" @click="downloadLog">{{ $t('csv.resultDialog.download') }}</v-btn>
                <v-btn color="primary" class="white--text" @click="close">{{ $t('button.cancel') }}</v-btn>
                <v-btn v-if="canSave" color="primary" @click="save">{{ $t('button.save') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="560" scrollable persistent>
        <v-card>
            <v-card-title class="headline">
                {{ $t('csv.resultDialog.editTitle') }}
            </v-card-title>
            <v-card-text>
                <v-alert v-if="editStructuralMsg" type="warning" dense outlined class="mb-3">
                    {{ editStructuralMsg }}
                </v-alert>
                <v-text-field
                    v-for="field in resolvedEditableFields"
                    :key="`edit-${field.value}`"
                    v-model="editForm[field.value]"
                    :label="field.text"
                    dense
                    outlined
                    class="mb-2"
                />
            </v-card-text>
            <v-card-actions>
                <v-btn text @click="closeRowEdit">{{ $t('button.cancel') }}</v-btn>
                <v-spacer />
                <v-btn color="primary" @click="saveRowEdit">{{ $t('csv.resultDialog.saveRow') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    </div>
</template>
<script>
import apiController from '@/utils/apiController';
import CSVItem from '../CSVItem.vue';

const NON_EDITABLE_HEADER_VALUES = new Set([
    'line_number',
    'error_line',
    'matched_cerca',
    'matched_pessoas',
    'acoes',
    'status',
    'erros',
]);

/** Duração da transição de saída do v-dialog (padrão do Vuetify). */
const DIALOG_LEAVE_TRANSITION_MS = 300;

export default {
    name: "ResultDialogCSV",
    components: {
        CSVItem,
    },
    props: {
        token: {
            type: String,
            default: ''
        },
        show: {
            type: Boolean,
            required: true
        },
        headers: {
            type: Array,
            default() {
                return [];
            }
        },
        result: {
            type: Object,
            default() {
                return {
                    error: true,
                    arqlog: "",
                    result: [],
                }
            }
        },
        /** When true, error rows can be edited or removed before save (Roteirizacao CSV flow). */
        allowRowActions: {
            type: Boolean,
            default: false,
        },
        /**
         * Field keys editable in the row dialog. If empty, derived from headers
         * (excluding line/error/match/action columns).
         */
        editableFields: {
            type: Array,
            default() {
                return [];
            },
        },
        /**
         * Optional (row) => void. Mutates the row after edits (e.g. clear/set error_line).
         */
        revalidateRow: {
            type: Function,
            default: null,
        },
    },
    data: () => ({
        mutableItems: [],
        editDialog: false,
        editRowKey: null,
        editStructuralMsg: '',
        editForm: {},
    }),
    computed: {
        itemKeyField() {
            return this.allowRowActions ? '_csvRowKey' : 'line_number';
        },
        errorRowCount() {
            return (this.items || []).filter((r) => this.rowHasError(r)).length;
        },
        message() {
            if (this.allowRowActions) {
                if (!this.items.length) {
                    return this.$t('csv.resultDialog.noRowsLeft');
                }
                if (this.errorRowCount > 0) {
                    return this.$t('system.messages.fileProcessedError');
                }
                return this.$t('system.messages.fileProcessedSuccess');
            }
            return this.result.error ? this.$t('system.messages.fileProcessedError') : this.$t('system.messages.fileProcessedSuccess');
        },
        messageIsError() {
            if (this.allowRowActions) {
                return !this.items.length || this.errorRowCount > 0;
            }
            return !!this.result.error;
        },
        canSave() {
            if (this.allowRowActions) {
                // Permite salvar mesmo com linhas incompletas/inválidas; elas permanecem marcadas.
                return this.items.length > 0;
            }
            return !this.result.error;
        },
        /** Show download button when server log (arqlog) or client-side log (logContent) is available */
        hasLogToDownload() {
            return !!(this.result.arqlog || (this.result.logContent && this.result.error));
        },
        sourceItems() {
            if (Array.isArray(this.result.result)) {
                return this.result.result;
            }
            if (this.result.result && Array.isArray(this.result.result.items)) {
                return this.result.result.items;
            }
            return [];
        },
        items() {
            if (this.allowRowActions) {
                return this.mutableItems;
            }
            return this.sourceItems;
        },
        displayHeaders() {
            const base = this.headers || [];
            if (!this.allowRowActions) return base;
            if (base.some((h) => h && h.value === 'acoes')) return base;
            return [
                ...base,
                {
                    text: this.$t('csv.resultDialog.actionsColumn'),
                    value: 'acoes',
                    sortable: false,
                    width: '148px',
                    align: 'center',
                },
            ];
        },
        headersWithProperties() {
            return this.displayHeaders.map(header => ({
                ...header,
                filtrable: true,
                sortable: header.sortable !== false
            }));
        },
        resolvedEditableFields() {
            if (Array.isArray(this.editableFields) && this.editableFields.length) {
                return this.editableFields.map((f) => {
                    if (typeof f === 'string') {
                        const header = (this.headers || []).find((h) => h.value === f);
                        return { value: f, text: (header && header.text) || f };
                    }
                    return f;
                });
            }
            return (this.headers || [])
                .filter((h) => h && h.value && !NON_EDITABLE_HEADER_VALUES.has(h.value) && h.editable !== false)
                .map((h) => ({ value: h.value, text: h.text || h.value }));
        },
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                if (val) {
                    this._cancelPendingClear();
                    // Síncrono (e não via queue): adiar deixaria o diálogo pintar
                    // um frame com a tabela vazia enquanto entra em cena.
                    if (this.allowRowActions) this.syncMutableItems();
                    return;
                }
                this.closeRowEdit();
                this._schedulePendingClear();
            },
        },
        // Ainda necessário: edições in-place do pai (novo csvResult) precisam
        // repopular a cópia local. Coalescido com o watcher de `show` para não
        // sincronizar duas vezes no mesmo flush ao abrir o diálogo.
        result: {
            deep: true,
            handler() {
                if (this.show && this.allowRowActions && !this.editDialog) {
                    this.queueSyncMutableItems();
                }
            },
        },
    },
    beforeDestroy() {
        this._cancelPendingClear();
    },
    methods: {
        rowHasError(item) {
            if (!item) return false;
            const err = item.error_line;
            return err != null && String(err).trim() !== '';
        },
        isEditedRow(item) {
            return !!(item && item._csvRowEdited) && !this.rowHasError(item);
        },
        editedRowWarning(item, header) {
            if (header && header.warning && item && item[header.warning]) {
                return item[header.warning];
            }
            if (!this.isEditedRow(item) || !header) return '';
            const addressFields = new Set([
                'logradouro',
                'endereco',
                'numero',
                'bairro',
                'cidade',
                'estado',
                'cep',
                'pais',
                'latitude',
                'longitude',
            ]);
            if (!addressFields.has(header.value)) return '';
            return this.$t('csv.resultDialog.editedRowHint');
        },
        /**
         * Ao abrir, os watchers `show` e `result` disparam no mesmo flush e
         * sincronizavam duas vezes. Aqui a segunda chamada do mesmo tick vira
         * no-op — `_syncedThisTick` é liberado no fim do flush.
         */
        queueSyncMutableItems() {
            if (this._syncedThisTick) return;
            this.syncMutableItems();
        },
        syncMutableItems() {
            // Chave determinística: com `Math.random()` cada sync gerava chaves
            // novas e o v-data-table destruía/recriava o DOM de todas as linhas.
            this.mutableItems = (this.sourceItems || []).map((r, i) => ({
                ...r,
                _csvRowKey: r._csvRowKey || `csv_${i}_${r.line_number}`,
            }));
            if (this._syncedThisTick) return;
            this._syncedThisTick = true;
            this.$nextTick(() => { this._syncedThisTick = false; });
        },
        /**
         * Libera a segunda cópia completa das linhas ao fechar. Adiado para o fim
         * da transição de saída do v-dialog, cujo conteúdo continua em tela
         * durante o fade — esvaziar na hora faria piscar "nenhuma linha restante".
         */
        _schedulePendingClear() {
            this._cancelPendingClear();
            if (!this.mutableItems.length) return;
            this._pendingClearTimer = setTimeout(() => {
                this._pendingClearTimer = null;
                if (this.show) return;
                this.mutableItems = [];
            }, DIALOG_LEAVE_TRANSITION_MS);
        },
        _cancelPendingClear() {
            if (!this._pendingClearTimer) return;
            clearTimeout(this._pendingClearTimer);
            this._pendingClearTimer = null;
        },
        baseName(str) {
            if (!str) return '';
            const normalized = String(str).replace(/\\/g, '/');
            return normalized.substring(normalized.lastIndexOf('/') + 1);
        },
        downloadLog() {
            if (this.result.logContent != null && this.result.logContent !== '') {
                const filename = this.result.logFilename || 'csv_import_log.csv';
                const blob = new Blob([this.result.logContent], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return;
            }
            if (!this.result.arqlog) return;
            apiController.request(apiController.endpoints.downloadLog, {
                params: { filename: this.baseName(this.result.arqlog) },
                config: { responseType: "blob" },
                silent: true,
                onSuccess: (response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", this.baseName(this.result.arqlog));
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                },
                onError: (e) => {
                    this.$emit('error', e);
                },
            });
        },
        close() {
            this.closeRowEdit();
            this.$emit('close');
        },
        getDisplayValue(header, item) {
            if (header.mask) {
                return header.mask;
            }

            const value = item?.[`${header.value}`];
            if ((header.value === 'shape' || header.value === 'geojson') && value) {
                return this.$t('system.messages.fieldTooLarge');
            }

            return value;
        },
        openRowEdit(item) {
            this.editRowKey = item._csvRowKey;
            const errText = String(item.error_line || '').toLowerCase();
            const structural =
                errText.includes('token') ||
                errText.includes('cabeçalho') ||
                errText.includes('encabezado') ||
                errText.includes('header') ||
                errText.includes('coluna') ||
                errText.includes('column');
            this.editStructuralMsg = structural
                ? this.$t('csv.resultDialog.structuralRowHint')
                : '';
            const form = {};
            this.resolvedEditableFields.forEach((f) => {
                const v = item[f.value];
                form[f.value] = v == null ? '' : String(v);
            });
            this.editForm = form;
            this.editDialog = true;
        },
        closeRowEdit() {
            this.editDialog = false;
            this.editRowKey = null;
            this.editStructuralMsg = '';
            this.editForm = {};
        },
        saveRowEdit() {
            const row = this.mutableItems.find((r) => r._csvRowKey === this.editRowKey);
            if (!row) {
                this.closeRowEdit();
                return;
            }
            this.resolvedEditableFields.forEach((f) => {
                const raw = this.editForm[f.value];
                const trimmed = raw == null ? '' : String(raw).trim();
                this.$set(row, f.value, trimmed === '' ? null : trimmed);
            });
            if (typeof this.revalidateRow === 'function') {
                this.revalidateRow(row);
            } else {
                this.$set(row, 'error_line', null);
            }
            this.$set(row, '_csvRowEdited', true);
            this.closeRowEdit();
        },
        deleteRow(item) {
            const idx = this.mutableItems.findIndex((r) => r._csvRowKey === item._csvRowKey);
            if (idx >= 0) {
                this.mutableItems.splice(idx, 1);
            }
        },
        stripRowKey(row) {
            if (!row || typeof row !== 'object') return row;
            const copy = { ...row };
            delete copy._csvRowKey;
            return copy;
        },
        save() {
            if (this.allowRowActions) {
                if (!this.canSave) return;
                const cleaned = this.mutableItems.map((row) => this.stripRowKey(row));
                const hasErrors = cleaned.some((r) => this.rowHasError(r));
                this.$emit('save', cleaned, {
                    ...this.result,
                    error: hasErrors,
                    result: cleaned,
                });
                return;
            }
            const raw = this.result.result;
            let payload;
            if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray(raw.boards)) {
                payload = raw;
            } else if (raw && typeof raw === 'object' && !Array.isArray(raw) && Array.isArray(raw.schedules)) {
                payload = raw;
            } else if (Array.isArray(this.result.schedules)) {
                payload = {
                    items: Array.isArray(raw) ? raw : (raw?.items || []),
                    schedules: this.result.schedules,
                    trips: this.result.trips || [],
                };
            } else if (Array.isArray(raw)) {
                payload = raw;
            } else if (raw && Array.isArray(raw.items)) {
                payload = raw.items;
            } else {
                payload = [];
            }
            this.$emit('save', payload, this.result);
        }
    }
}
</script>
<style>
.csv-result-row--edited > td {
    background-color: #fff3e0 !important;
}
.csv-result-row--error > td {
    background-color: #ffebee !important;
}
</style>
